import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useOrganization, useUpdateOrganization, useUploadOrganizationLogo } from '@/hooks/useOrganizations';
import { PageSpinner } from '@/components/ui/Spinner';
import { Alert, ErrorState } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { getApiErrorMessage } from '@/api/client';

/** The organization's own details: name, description and the logo its competitions inherit. */
export function OrganizerOrganizationPage() {
  const { organizationId = '' } = useParams<{ organizationId: string }>();
  const organization = useOrganization(organizationId);
  const update = useUpdateOrganization(organizationId);
  const uploadLogo = useUploadOrganizationLogo(organizationId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (organization.data) {
      setName(organization.data.name);
      setDescription(organization.data.description ?? '');
    }
  }, [organization.data]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await update.mutateAsync({ name: name.trim(), description: description.trim() || undefined });
    setSaved(true);
  }

  async function handleLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadLogo.mutateAsync(file);
    e.target.value = '';
  }

  if (organization.isLoading) return <PageSpinner />;
  if (organization.isError || !organization.data) {
    return <ErrorState message={getApiErrorMessage(organization.error)} onRetry={() => organization.refetch()} />;
  }
  const org = organization.data;

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-fg">Nastavitve organizacije</h1>
        <p className="text-sm text-fg-muted">Ime in logotip se prikažeta na razpisih in straneh vseh vaših tekmovanj.</p>
      </div>

      <Card>
        <CardBody>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3" onChange={() => setSaved(false)}>
            {update.isError && <Alert variant="error">{getApiErrorMessage(update.error)}</Alert>}
            <Input label="Ime organizacije" value={name} onChange={(e) => setName(e.target.value)} required />
            <Input label="Opis (neobvezno)" value={description} onChange={(e) => setDescription(e.target.value)} />
            <div className="flex items-center gap-3">
              <Button type="submit" isLoading={update.isPending} disabled={!name.trim()}>
                Shrani
              </Button>
              {saved && <span className="text-sm text-success">Shranjeno.</span>}
            </div>

            <div className="flex flex-col gap-2 border-t border-line pt-3">
              {uploadLogo.isError && <Alert variant="error">{getApiErrorMessage(uploadLogo.error)}</Alert>}
              <div className="flex items-center gap-3">
                {org.logoUrl ? (
                  <img src={org.logoUrl} alt={`${org.name} logo`} className="h-12 w-12 rounded-photo border border-line object-contain" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-photo border border-dashed border-line-strong text-xs text-fg-muted">
                    Brez logotipa
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-fg-secondary">
                    Logotip organizacije
                  </label>
                  <p className="text-xs text-fg-muted">
                    PNG, JPG ali WebP · 200–2000 px na stranico · do 2 MB · priporočeno kvadratno, npr. 512 × 512 px, PNG s prosojnim ozadjem.
                  </p>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={uploadLogo.isPending}
                    onChange={handleLogo}
                    className="text-sm text-fg-secondary file:mr-3 file:h-8 file:rounded-control file:border file:border-line-strong file:bg-surface-2 file:px-3 file:text-[13px] file:font-medium file:text-fg hover:file:bg-surface-3"
                  />
                </div>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
